<template>
    <div>
      <!-- ✅ DIALOG PRINCIPAL PARA PENDÊNCIAS POR ORDEM -->
      <v-dialog v-model="dialogVisible" max-width="1400" :fullscreen="mobile">
        <v-card>
          <!-- Header melhorado -->
          <v-card-title class="bg-gradient-warning text-white d-flex align-center pa-4">
            <v-avatar color="white" class="mr-3">
              <v-icon color="warning">mdi-alert-circle</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-h5 font-weight-bold">{{ titulo }}</div>
              <div class="text-subtitle-1 opacity-90">
                {{ pendenciasLocais.length }} movimentação(ões) aguardando autorização
              </div>
            </div>
            <v-btn 
              icon 
              variant="text" 
              @click="fecharDialog"
              size="large"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
  
          <v-card-text class="pa-4">
            <!-- Tabela melhorada -->
            <v-table v-if="pendenciasLocais.length > 0" class="elevation-2 rounded-lg">
              <thead>
                <tr class="bg-grey-lighten-4">
                  <th class="text-center font-weight-bold">ID</th>
                  <th class="text-center font-weight-bold">Ordem SAP</th>
                  <th class="text-center font-weight-bold">Movimentação</th>
                  <th class="text-center font-weight-bold">Item</th>
                  <th class="text-center font-weight-bold">Qtd</th>
                  <th class="text-center font-weight-bold">Data</th>
                  <th class="text-center font-weight-bold">Observação</th>
                  <th class="text-center font-weight-bold">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pendencia in pendenciasLocais" :key="pendencia.id" class="hover-row">
                  <!-- ID -->
                  <td class="text-center">
                    <v-chip color="blue-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-identifier</v-icon>
                      {{ pendencia.id }}
                    </v-chip>
                  </td>
  
                  <!-- Ordem SAP -->
                  <td class="text-center">
                    <v-chip color="orange-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-clipboard-text</v-icon>
                      {{ pendencia.ordem_producao?.id_ordem_sap || 'N/A' }}
                    </v-chip>
                  </td>
  
                  <!-- Movimentação (De → Para) -->
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <v-chip size="small" color="purple-darken-1" variant="outlined" class="mr-1">
                        {{ pendencia.setor_origem?.nome || 'N/A' }}
                      </v-chip>
                      <v-icon size="small" class="mx-2" color="grey-darken-1">mdi-arrow-right-bold</v-icon>
                      <v-chip size="small" color="orange-darken-1" variant="outlined" class="ml-1">
                        {{ pendencia.setor_destino?.nome || 'N/A' }}
                      </v-chip>
                    </div>
                  </td>
  
                  <!-- Item (Parte + Tamanho) -->
                  <td class="text-center">
                    <div class="text-body-2">
                      <div class="font-weight-bold text-primary">{{ pendencia.parte || 'N/A' }}</div>
                      <v-chip size="x-small" color="grey-darken-1" variant="outlined">
                        {{ pendencia.tamanho || 'N/A' }}
                      </v-chip>
                    </div>
                  </td>
  
                  <!-- Quantidade -->
                  <td class="text-center">
                    <v-chip color="green-darken-1" variant="flat" size="small">
                      <v-icon start size="small">mdi-counter</v-icon>
                      {{ pendencia.quantidade || 0 }}
                    </v-chip>
                  </td>
  
                  <!-- Data -->
                  <td class="text-center">
                    <v-chip color="indigo" variant="flat" size="small">
                      <v-icon start size="small">mdi-calendar</v-icon>
                      {{ formatarDataPendencia(pendencia.data_registro) }}
                    </v-chip>
                  </td>
  
                  <!-- Observação -->
                  <td class="text-center" style="max-width: 150px;">
                    <v-tooltip v-if="pendencia.observacao" :text="pendencia.observacao">
                      <template v-slot:activator="{ props }">
                        <v-chip 
                          v-bind="props"
                          size="small" 
                          color="grey-darken-1" 
                          variant="outlined"
                          class="text-truncate"
                        >
                          <v-icon start size="small">mdi-note-text</v-icon>
                          {{ pendencia.observacao.substring(0, 10) }}...
                        </v-chip>
                      </template>
                    </v-tooltip>
                    <v-chip v-else size="small" color="grey-lighten-2" variant="outlined">
                      <v-icon size="small">mdi-note-off</v-icon>
                    </v-chip>
                  </td>
  
                  <!-- Ações -->
                  <td class="text-center">
                    <v-btn 
                      color="success" 
                      size="small" 
                      variant="flat"
                      rounded="lg"
                      :loading="loadingAutorizacao === pendencia.id"
                      @click="confirmarAutorizacao(pendencia)"
                    >
                      <v-icon start size="small">mdi-check-bold</v-icon>
                      Autorizar##
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
  
            <!-- Estado vazio melhorado -->
            <div v-else class="text-center pa-8">
              <v-avatar size="120" color="green-lighten-4" class="mb-4">
                <v-icon size="60" color="green-lighten-1">mdi-clipboard-check-outline</v-icon>
              </v-avatar>
              <h3 class="text-h5 mb-2 text-green-darken-1">{{ mensagemVazia }}</h3>
              <p class="text-body-1 text-grey">
                {{ descricaoVazia }}
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
  
      <!-- ✅ DIÁLOGO DE CONFIRMAÇÃO MELHORADO -->
      <v-dialog v-model="dialogConfirmarAutorizacao" max-width="600" persistent>
        <v-card class="elevation-12">
          <!-- Header com gradiente -->
          <v-card-title class="bg-gradient-success text-white d-flex align-center pa-4">
            <v-avatar color="white" class="mr-3">
              <v-icon color="success">mdi-check-circle</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-h6 font-weight-bold">Confirmar Autorização</div>
              <div class="text-subtitle-2 opacity-90">Revisar dados da movimentação</div>
            </div>
            <v-btn 
              icon 
              variant="text" 
              @click="dialogConfirmarAutorizacao = false"
              size="small"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
  
          <v-card-text class="pa-6">
            <!-- Pergunta principal -->
            <div class="text-h6 mb-4 d-flex align-center">
              <v-icon class="mr-2" color="warning">mdi-help-circle</v-icon>
              Deseja realmente autorizar esta movimentação?
            </div>
  
            <!-- Card com dados da pendência -->
            <v-card v-if="pendenciaSelecionada" variant="outlined" class="mb-4">
              <v-card-text class="pa-4">
                <v-row dense>
                  <!-- ID e Ordem -->
                  <v-col cols="12" md="6">
                    <div class="info-group">
                      <div class="info-label">ID da Movimentação</div>
                      <v-chip color="blue-darken-1" variant="flat" size="small">
                        <v-icon start size="small">mdi-identifier</v-icon>
                        {{ pendenciaSelecionada.id }}
                      </v-chip>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <div class="info-group">
                      <div class="info-label">Ordem SAP</div>
                      <v-chip color="orange-darken-1" variant="flat" size="small">
                        <v-icon start size="small">mdi-clipboard-text</v-icon>
                        {{ pendenciaSelecionada.ordem_producao?.id_ordem_sap || 'N/A' }}
                      </v-chip>
                    </div>
                  </v-col>
  
                  <!-- Cliente -->
                  <v-col cols="12">
                    <div class="info-group">
                      <div class="info-label">Cliente</div>
                      <div class="info-value">
                        <v-icon color="primary" size="small" class="mr-2">mdi-domain</v-icon>
                        {{ pendenciaSelecionada.ordem_producao?.detalhes?.cliente?.nome || 'N/A' }}
                      </div>
                    </div>
                  </v-col>
  
                  <!-- Movimentação -->
                  <v-col cols="12">
                    <div class="info-group">
                      <div class="info-label">Movimentação</div>
                      <div class="d-flex align-center">
                        <v-chip size="small" color="purple-darken-1" variant="outlined" class="mr-2">
                          {{ pendenciaSelecionada.setor_origem?.nome || 'N/A' }}
                        </v-chip>
                        <v-icon size="small" color="grey-darken-1">mdi-arrow-right-bold</v-icon>
                        <v-chip size="small" color="orange-darken-1" variant="outlined" class="ml-2">
                          {{ pendenciaSelecionada.setor_destino?.nome || 'N/A' }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>
  
                  <!-- Item -->
                  <v-col cols="12" md="4">
                    <div class="info-group">
                      <div class="info-label">Parte</div>
                      <div class="info-value font-weight-bold text-primary">
                        <v-icon color="primary" size="small" class="mr-2">mdi-puzzle</v-icon>
                        {{ pendenciaSelecionada.parte || 'N/A' }}
                      </div>
                    </div>
                  </v-col>
  
                  <v-col cols="12" md="4">
                    <div class="info-group">
                      <div class="info-label">Tamanho</div>
                      <v-chip color="grey-darken-1" variant="outlined" size="small">
                        {{ pendenciaSelecionada.tamanho || 'N/A' }}
                      </v-chip>
                    </div>
                  </v-col>
  
                  <v-col cols="12" md="4">
                    <div class="info-group">
                      <div class="info-label">Quantidade</div>
                      <v-chip color="green-darken-1" variant="flat" size="small">
                        <v-icon start size="small">mdi-counter</v-icon>
                        {{ pendenciaSelecionada.quantidade || 0 }}
                      </v-chip>
                    </div>
                  </v-col>
  
                  <!-- Observação (se houver) -->
                  <v-col cols="12" v-if="pendenciaSelecionada.observacao">
                    <div class="info-group">
                      <div class="info-label">Observação</div>
                      <div class="info-value">
                        <v-icon color="info" size="small" class="mr-2">mdi-note-text</v-icon>
                        {{ pendenciaSelecionada.observacao }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
  
            <!-- Alerta informativo -->
            <v-alert 
              type="info" 
              variant="tonal" 
              class="mb-4"
              border="start"
              border-color="info"
            >
              <div class="text-body-2">
                <strong>Atenção:</strong> Esta ação irá autorizar a movimentação e permitir que ela seja processada.
              </div>
            </v-alert>
          </v-card-text>
  
          <!-- Botões de ação -->
          <v-card-actions class="pa-6 pt-0">
            <v-spacer></v-spacer>
            <v-btn 
              color="grey-darken-1" 
              variant="outlined" 
              @click="dialogConfirmarAutorizacao = false"
              class="mr-3"
            >
              <v-icon start>mdi-close</v-icon>
              Cancelar
            </v-btn>
            <v-btn 
              color="success" 
              variant="flat"
              @click="executarAutorizacao"
              :loading="loadingConfirmacao"
              size="large"
            >
              <v-icon start>mdi-check-bold</v-icon>
              Confirmar Autorização
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import { useDisplay } from 'vuetify'
  
  export default {
    name: 'PendenciasPorOrdemSetorDialog',
    
    props: {
      // Controle do dialog
      modelValue: {
        type: Boolean,
        default: false
      },
      
      // Dados das pendências
      pendencias: {
        type: Array,
        default: () => []
      },
      
      // ID do chefe/usuário para autorização
      idChefe: {
        type: [Number, String],
        required: true
      },
      
      // Informações da ordem (opcional)
      ordemInfo: {
    type: Object,
    default: () => ({})
  },

      
      // Textos customizáveis
      titulo: {
        type: String,
        default: 'Pendências da Ordem'
      },
      
      mensagemVazia: {
        type: String,
        default: 'Tudo Autorizado!'
      },
      
      descricaoVazia: {
        type: String,
        default: 'Não há pendências para esta ordem.'
      }
    },
    
    emits: ['update:modelValue', 'pendencia-autorizada', 'fechar'],
    
    data() {
      return {
        // Controle dos dialogs
        dialogConfirmarAutorizacao: false,
        
        // Dados locais
        pendenciaSelecionada: null,
        loadingAutorizacao: null,
        loadingConfirmacao: false,
        
        // Lista local de pendências (para manipulação)
        pendenciasLocais: []
      }
    },
    
    setup() {
      const { mobile } = useDisplay()
      return { mobile }
    },
    
    watch: {
      // Sincronizar pendências quando mudarem
      pendencias: {
        handler(novasPendencias) {
          this.pendenciasLocais = [...novasPendencias]
        },
        immediate: true
      }
    },
    
    computed: {
      dialogVisible: {
        get() {
          return this.modelValue
        },
        set(value) {
          this.$emit('update:modelValue', value)
        }
      }
    },
    
    methods: {
      // Fechar dialog
      fecharDialog() {
        this.dialogConfirmarAutorizacao = false
        this.pendenciaSelecionada = null
        this.loadingAutorizacao = null
        this.loadingConfirmacao = false
        
        this.$emit('update:modelValue', false)
        this.$emit('fechar')
      },
      
      // Formatar data
      formatarDataPendencia(dataString) {
        if (!dataString) return 'N/A'
        
        try {
          const data = new Date(dataString)
          return data.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          })
        } catch {
          return dataString
        }
      },
      
      // Abrir dialog de confirmação
      confirmarAutorizacao(pendencia) {
        this.pendenciaSelecionada = pendencia
        this.dialogConfirmarAutorizacao = true
      },
      
      // Executar autorização
      async executarAutorizacao() {
        if (!this.pendenciaSelecionada) return
        
        try {
          this.loadingConfirmacao = true
          this.loadingAutorizacao = this.pendenciaSelecionada.id
          
          // Emitir evento para o pai processar
          this.$emit('pendencia-autorizada', this.pendenciaSelecionada)
          
          // Remover da lista local
          this.pendenciasLocais = this.pendenciasLocais.filter(
            p => p.id !== this.pendenciaSelecionada.id
          )
          
          // Fechar dialog de confirmação
          this.dialogConfirmarAutorizacao = false
          this.pendenciaSelecionada = null
          
          console.log('Pendência autorizada com sucesso!')
          
        } catch (error) {
          console.error('Erro ao autorizar pendência:', error)
          console.log('Erro ao autorizar movimentação. Tente novamente.')
        } finally {
          this.loadingConfirmacao = false
          this.loadingAutorizacao = null
        }
      }
    }
  }
  </script>
  
  <style scoped>
  /* ✅ GRADIENTES */
  .bg-gradient-warning {
    background: linear-gradient(135deg, #ff9800 0%, #ff5722 100%);
    box-shadow: 0 4px 20px rgba(255, 152, 0, 0.3);
  }
  
  .bg-gradient-success {
    background: linear-gradient(135deg, #4caf50 0%, #66bb6a 50%, #81c784 100%);
    box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3);
  }
  
  /* ✅ HOVER EFFECTS */
  .hover-row:hover {
    background-color: rgba(0, 0, 0, 0.02);
    transition: background-color 0.2s ease;
  }
  
  .v-btn:hover {
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }
  
  .v-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease;
  }
  
  /* ✅ GRUPOS DE INFORMAÇÃO */
  .info-group {
    margin-bottom: 12px;
  }
  
  .info-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .info-value {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
    display: flex;
    align-items: center;
  }
  
  /* ✅ ELEVAÇÃO */
  .elevation-12 {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
  }
  </style>